const API_ROOT = '/api/v1';

export interface ApiEnvelope<T> {
  data: T;
  meta?: Record<string, unknown>;
}

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  version?: number;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, version: entityVersion, ...fetchOptions } = options;
  const headers = new Headers(options.headers);
  headers.set('Accept', 'application/json');
  if (body !== undefined) headers.set('Content-Type', 'application/json');
  if (entityVersion !== undefined) headers.set('If-Match', `"${entityVersion}"`);

  const requestInit: RequestInit = {
    ...fetchOptions,
    headers,
    credentials: 'same-origin',
  };
  if (body !== undefined) requestInit.body = JSON.stringify(body);
  const response = await fetch(`${API_ROOT}${path}`, requestInit);

  if (response.status === 204) return undefined as T;
  const payload = (await response.json().catch(() => null)) as
    ApiEnvelope<T> | { error?: { code?: string; message?: string; details?: unknown } } | T | null;
  if (!response.ok) {
    const failure =
      payload && typeof payload === 'object' && 'error' in payload ? payload.error : null;
    if (response.status === 401 && failure?.code === 'AUTH_REQUIRED') {
      window.dispatchEvent(new CustomEvent('life:auth-required'));
    }
    throw new ApiError(
      response.status,
      failure?.code ?? 'INTERNAL',
      failure?.message ?? `Request failed (${response.status})`,
      failure?.details,
    );
  }
  if (payload && typeof payload === 'object' && 'data' in payload) return payload.data;
  return payload as T;
}

export function listFrom<T>(value: unknown, preferredKeys: string[] = []): T[] {
  if (Array.isArray(value)) return value as T[];
  if (!value || typeof value !== 'object') return [];
  const record = value as Record<string, unknown>;
  for (const key of [...preferredKeys, 'items', 'results', 'records', 'data']) {
    if (Array.isArray(record[key])) return record[key] as T[];
  }
  return [];
}

export function downloadUrl(path: string): string {
  return `${API_ROOT}${path}`;
}

export function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

export function field<T = unknown>(value: unknown, ...keys: string[]): T | undefined {
  const record = asRecord(value);
  for (const key of keys) if (record[key] !== undefined) return record[key] as T;
  return undefined;
}
